import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Check } from "lucide-react";
import { useCart } from "../context/CartContext";
import { MaskText, Reveal } from "./Reveal";
import { SKIN, WELLNESS, PEPTIDES, PAYMENT, IMG } from "../data/products";
import { scrollToId } from "./SmoothScroll";

const money = (n) => `$${Number(n).toFixed(2).replace(/\.00$/, "")}`;

function AddButton({ onAdd, testId }) {
  const [done, setDone] = useState(false);
  const click = () => {
    onAdd();
    setDone(true);
    setTimeout(() => setDone(false), 1100);
  };
  return (
    <button
      onClick={click}
      className="btn-ink rounded-full w-11 h-11 flex items-center justify-center shrink-0"
      aria-label="Add to cart"
      data-testid={testId}
    >
      {done ? <Check size={16} strokeWidth={2} /> : <Plus size={16} strokeWidth={2} />}
    </button>
  );
}

function ProductCard({ p, wide }) {
  const { addItem } = useCart();
  return (
    <Reveal
      className={wide ? "sm:col-span-2" : ""}
      y={40}
    >
      <div className="group" data-testid={`product-${p.id}`}>
        <div
          className={`frame relative ${wide ? "aspect-[16/9]" : "aspect-[4/5]"}`}
          data-cursor="hover"
        >
          <img
            src={p.img}
            alt={p.name}
            loading="lazy"
            className="w-full h-full object-cover"
          />
          {p.tag && (
            <span
              className="absolute top-4 left-4 overline px-3 py-1.5 rounded-full"
              style={
                p.accent
                  ? { backgroundColor: p.accent, color: "#fff" }
                  : { backgroundColor: "var(--bone)" }
              }
            >
              {p.tag}
            </span>
          )}
          {p.compareAt && (
            <span className="absolute top-4 right-4 overline bg-[var(--terra)] text-white px-3 py-1.5 rounded-full">
              Save ${(p.compareAt - p.price).toFixed(0)}
            </span>
          )}
        </div>
        <div className="flex items-start justify-between gap-4 mt-5">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl leading-none tracking-tight">
              {p.name}
            </h3>
            <p className="overline text-[var(--ink-soft)] mt-2">{p.sub}</p>
            <p className="font-body text-sm text-[var(--ink-soft)] mt-3 max-w-xs leading-relaxed">
              {p.desc}
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="flex items-baseline gap-2 justify-end">
              {p.compareAt && (
                <span className="font-mono text-sm text-[var(--ink-soft)] line-through">
                  {money(p.compareAt)}
                </span>
              )}
              <span className="font-display text-3xl">{money(p.price)}</span>
            </div>
            <div className="mt-3 flex justify-end">
              <AddButton
                testId={`add-${p.id}`}
                onAdd={() => addItem({ id: p.id, name: p.name, price: p.price, img: p.img })}
              />
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

function PeptideCard({ p }) {
  const { addItem } = useCart();
  const [mode, setMode] = useState("vial");
  const isFluid = p.id.startsWith("bacwater");
  const price = mode === "kit" ? p.kit : p.price;
  const label = mode === "kit" ? `${p.name} — Kit (10 ct)` : p.name;

  return (
    <Reveal y={40}>
      <div className="group border border-[var(--line)] p-5 bg-[var(--bone)] text-[var(--ink)]" data-testid={`product-${p.id}`}>
        <div className="frame aspect-square relative" data-cursor="hover">
          <img src={p.img} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
          <span className="absolute top-3 left-3 overline bg-[var(--ink)] text-[var(--bone)] px-3 py-1 rounded-full">
            {p.mg}
          </span>
          {p.popular && (
            <span className="absolute top-3 right-3 overline bg-[var(--terra)] text-white px-3 py-1 rounded-full" data-testid={`bestseller-${p.id}`}>
              Bestseller
            </span>
          )}
        </div>
        <div className="mt-4">
          <h3 className="font-display text-xl leading-none tracking-tight text-[var(--ink)]" data-testid={`peptide-title-${p.id}`}>{p.name}</h3>
          <p className="font-body text-xs text-[var(--ink-soft)] mt-2">
            {p.desc}
          </p>

          {!isFluid && (
            <div className="mt-4 flex gap-2">
              {["vial", "kit"].map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`overline px-3 py-2 rounded-full border transition-colors ${
                    mode === m
                      ? "bg-[var(--ink)] text-[var(--bone)] border-[var(--ink)]"
                      : "border-[var(--line)] text-[var(--ink-soft)]"
                  }`}
                  data-testid={`variant-${p.id}-${m}`}
                >
                  {m === "vial" ? "Single vial" : "Kit ×10"}
                </button>
              ))}
            </div>
          )}
          {isFluid && (
            <div className="mt-4 flex gap-2">
              {["vial", "kit"].map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`overline px-3 py-2 rounded-full border transition-colors ${
                    mode === m
                      ? "bg-[var(--ink)] text-[var(--bone)] border-[var(--ink)]"
                      : "border-[var(--line)] text-[var(--ink-soft)]"
                  }`}
                  data-testid={`variant-${p.id}-${m}`}
                >
                  {m === "vial" ? "Single" : "10 ct"}
                </button>
              ))}
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <span className="font-display text-3xl text-[var(--ink)]" data-testid={`peptide-price-${p.id}`}>{money(price)}</span>
            <AddButton
              testId={`add-${p.id}`}
              onAdd={() =>
                addItem({
                  id: p.id,
                  name: label,
                  variant: mode === "kit" ? "Kit (10 ct)" : p.mg,
                  price,
                  img: p.img,
                })
              }
            />
          </div>
        </div>
      </div>
    </Reveal>
  );
}

function SectionHead({ overline, lines, note }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 sm:mb-16 gap-6">
      <div>
        <p className="overline text-[var(--terra)] mb-5">{overline}</p>
        <MaskText
          as="h2"
          lines={lines}
          className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.88] tracking-tighter"
        />
      </div>
      {note && (
        <Reveal delay={0.15}>
          <p className="font-body text-sm text-[var(--ink-soft)] max-w-xs md:text-right leading-relaxed">
            {note}
          </p>
        </Reveal>
      )}
    </div>
  );
}

export default function Catalog() {
  return (
    <div className="bg-[var(--bone)]">
      {/* PEPTIDES */}
      <section id="peptides" className="py-24 sm:py-36 bg-[var(--ink)] text-[var(--bone)]" data-testid="section-peptides">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 sm:mb-16 gap-6">
            <div>
              <p className="overline text-[var(--terra-soft)] mb-5">Chapter 05 — Research</p>
              <MaskText
                as="h2"
                lines={["Research", "peptides."]}
                className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.88] tracking-tighter"
              />
            </div>
            <Reveal delay={0.15}>
              <div className="md:text-right">
                <p className="font-body text-sm text-white/60 max-w-xs leading-relaxed">
                  By the vial or the kit. Wholesale pricing available for verified partners with MOQ.
                </p>
                <button
                  onClick={() => scrollToId("wholesale")}
                  className="btn-outline mt-4 px-5 py-3 rounded-full !border-white/40 !text-[var(--bone)] hover:!bg-[var(--bone)] hover:!text-[var(--ink)]"
                  data-testid="peptide-wholesale-btn"
                >
                  Inquire wholesale
                </button>
              </div>
            </Reveal>
          </div>

          <Reveal className="mb-12">
            <div className="frame aspect-[21/9] border border-white/10">
              <img src={IMG.vialsHero} alt="Balance research vials" className="w-full h-full object-cover" />
            </div>
          </Reveal>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {PEPTIDES.map((p) => (
              <PeptideCard key={p.id} p={p} />
            ))}
          </div>
          <p className="font-mono text-xs text-white/40 mt-10 max-w-xl">
            Research compounds are sold strictly for laboratory research purposes and
            are not for human consumption. Wholesale MOQ inquiries: {PAYMENT.wholesaleEmail}
          </p>
        </div>
      </section>

      {/* SKIN */}
      <section id="skin" className="py-24 sm:py-36 max-w-[1500px] mx-auto px-5 sm:px-8" data-testid="section-skin">
        <SectionHead
          overline="Chapter 06 — Skin"
          lines={["The skin", "formulary."]}
          note="Retinoids, brighteners and correctors. Every strength of tretinoin, plus the trio bundle."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
          {SKIN.map((p) => (
            <ProductCard key={p.id} p={p} wide={p.wide} />
          ))}
        </div>
      </section>

      {/* WELLNESS */}
      <section id="wellness" className="py-24 sm:py-36 bg-[var(--bone-2)]" data-testid="section-wellness">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8">
          <SectionHead
            overline="Chapter 07 — Wellness"
            lines={["Protocol", "essentials."]}
            note="Antiparasitic and supportive compounds for your wellness routine."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {WELLNESS.map((p) => (
              <ProductCard key={p.id} p={p} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
